import { Check } from "lucide-react";
import { cn } from "@/lib/utils";


interface OneTimePlan {
  name: string;
  price: string;
  priceWithoutVat: string;
  description: string;
  url: string;
  highlighted?: boolean;
  features: string[];
}

const oneTimePlans: OneTimePlan[] = [
  {
    name: "Branding",
    price: "500",
    priceWithoutVat: "413,22",
    description: "Logo, brandbook e identidad visual completa.",
    url: "https://buy.stripe.com/00w28reXkePvg5ZgfY0480o",
    features: [
      "Logo y variantes",
      "Brandbook completo",
      "Paleta y tipografías",
    ],
  },
  {
    name: "CRM",
    price: "900",
    priceWithoutVat: "743,80",
    description: "Setup completo de Kommo: pipeline, automatizaciones y formación.",
    url: "https://buy.stripe.com/dRmfZh6qO8r79HB5Bk0480p",
    highlighted: true,
    features: [
      "Setup completo de Kommo",
      "Pipeline y automatizaciones",
      "Formación del equipo",
    ],
  },
];

const OneTimePricingSection = () => {
  const renderPlan = (plan: OneTimePlan) => (
    <div
      key={plan.name}
      className={cn(
        "relative flex flex-col rounded-2xl border bg-card p-8 transition-all",
        plan.highlighted
          ? "border-primary shadow-xl shadow-primary/10 lg:-translate-y-2"
          : "border-border hover:border-primary/40"
      )}
    >
      {plan.highlighted && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full uppercase tracking-wide">
          Más popular
        </span>
      )}

      <div className="mb-4">
        <h3 className="font-display text-2xl uppercase mb-2">{plan.name}</h3>
        <p className="text-sm text-muted-foreground min-h-[40px]">{plan.description}</p>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-1 flex-wrap">
          <span className="text-5xl font-display">{plan.price}€</span>
          <span className="text-muted-foreground text-sm">IVA incluido</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {plan.priceWithoutVat}€ + IVA
        </p>
        <p className="text-xs text-primary font-medium mt-2">
          o 3 cuotas sin intereses
        </p>
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3 text-sm">
            <Check size={18} className="text-primary shrink-0 mt-0.5" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <a
        href={plan.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Contratar ${plan.name}`}
        className={cn(
          "w-full inline-flex items-center justify-center h-11 rounded-md text-sm font-medium transition-colors",
          plan.highlighted
            ? "bg-primary text-primary-foreground hover:bg-primary/90"
            : "border border-input bg-background hover:bg-accent hover:text-accent-foreground"
        )}
      >
        Contratar
      </a>
    </div>
  );

  return (
    <section id="one-time-pricing" className="relative py-20 md:py-32 scroll-mt-16 bg-muted/30 overflow-hidden">

      <div className="container px-6">
        <div className="max-w-2xl mx-auto text-center mb-12">
          <h2 className="font-display text-4xl md:text-6xl uppercase mb-4">
            Servicios de pago único
          </h2>
          <p className="text-lg text-muted-foreground mb-4">
            Te los financiamos<br />
            <span className="glow-pulse font-semibold">3 meses 0% de interés.</span>
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div data-reveal-stagger className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            {oneTimePlans.map((plan) => renderPlan(plan))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default OneTimePricingSection;
